import React, { useState, useCallback, useEffect } from 'react';
import InvestorPortal from '../components/InvestorPortal';
import RoleSelectScreen from './RoleSelectScreen';
import WelcomeScreen from './WelcomeScreen';
import TenantPortal from './tenant/TenantPortal';
import GuestPortal from './guest/GuestPortal';
import CEOPortal from './ceo/CEOPortal';
import DemoShell from './demo/DemoShell';
import PartnerPortal from './partner/PartnerPortal';
import OwnerPMPreview from './owner/OwnerPMPreview';
import { AppRole, ROLE_STORAGE_KEY } from './shared/theme';
import { isFilmMode } from './demo/demoMode';
import { installDemoNetworkBlock } from './demo/demoNetwork';

// ============================================================
// RootRouter — sits above the existing InvestorPortal and routes
// the app by the chosen role (Property Owner / Tenant / Guest / CEO).
//   owner  -> existing InvestorPortal (+ Property Management tab)
//   tenant -> TenantPortal
//   guest  -> GuestPortal (Airbnb short-stay booking)
//   ceo    -> CEOPortal (company performance, read from the CRM SaaS
//             company portal — see portals/ceo/ceoApi.ts)
//   partner-> PartnerPortal (Cooperation / Referral Partner). Signs in with the
//             SAME account as owner and shares its session, so an owner who is
//             already signed in walks straight in.
//   demo   -> DemoShell (code-gated showcase: after the access code, all four
//             views above run on bundled sample data with API traffic blocked)
//
// FILM MODE (isFilmMode, preview channels only) short-circuits the owner and
// tenant routes onto the same sample fixtures the showcase uses, but through
// the ordinary entry screen and with no demo chrome — so a product video
// records the live app's view. It cannot switch on anywhere else; see the
// hostname argument in demoMode.ts.
//
// EA / staff is NOT a separate role. Staff log in through the normal
// role logins; when their account is flagged admin, their overview
// renders in Admin mode (EAAdminConsole) inside that role.
// ============================================================

/**
 * Does the URL ask for the full role list instead of the welcome screen?
 *
 * The list is no longer a screen anyone LANDS on — there is deliberately no
 * link to it, because a visitor should meet one way in, not a menu. Staff
 * reach it by typing the hatch, the same one that has always existed:
 *   ?roles=all / #roles   every role row (Guest, Partner, CEO, "What we offer")
 *   ?demo=1    / #demo    the code-gated showcase, whose ONLY door is that list
 *
 * Deliberately NOT showAllRoles() / isDemoEntryVisible(): both of those return
 * true for the whole of local dev, which would make the welcome screen
 * unreachable on the machine where it is being worked on. This reads the URL
 * and nothing else, so dev and production behave identically. The row-level
 * gating inside RoleSelectScreen is unchanged and still does its own job.
 */
function entryAsksForRoleList(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('roles') === 'all') return true;
    if (params.get('demo') === '1') return true;
    const hash = window.location.hash.replace('#', '').toLowerCase();
    return hash === 'roles' || hash === 'demo';
  } catch {
    return false;
  }
}

const RootRouter: React.FC = () => {
  // Always start at the entry screen on app open — WelcomeScreen, which hands
  // to the owner sign-in, where RoleSwitch offers Tenant. The role is still
  // chosen BEFORE any login, so each role keeps its own login design. It is
  // held in memory for this session only and is intentionally not remembered
  // across launches.
  const [role, setRole] = useState<AppRole | null>(null);
  // Standalone owner Property Management preview (no live backend).
  const [ownerPreview, setOwnerPreview] = useState(false);
  // The app opens on WelcomeScreen. The full role list is still here and
  // unchanged — it is just no longer the first thing a visitor meets, and
  // nothing on screen links to it. See entryAsksForRoleList above.
  const [showRoles, setShowRoles] = useState(entryAsksForRoleList);

  // Recording a product video: the portals run on sample fixtures. The network
  // block is installed here rather than left to DemoShell, because film mode
  // bypasses that shell — without it a portal could still reach the backend.
  const filming = isFilmMode();
  useEffect(() => {
    if (!filming) return;
    return installDemoNetworkBlock();
  }, [filming]);

  // Device column when filming on a desktop. Below the frame's own width the
  // window is already that shape and the app's breakpoints do the right thing,
  // so the classes come off and nothing is constrained twice.
  useEffect(() => {
    if (!filming) return;
    // THE DEMO IS A TABLET BY DEFAULT: 1024 x 1366, iPad Pro portrait.
    //
    // THE WIDTH IS NOT A TASTE DECISION. This app has exactly two layouts —
    // a phone layout below 900px and a desktop layout at 900px and up — and
    // no tablet layout in between. Because media queries read the browser
    // WINDOW rather than the frame, a frame wider than the phone gate always
    // gets the DESKTOP layout. So the frame has to be wide enough for that
    // layout to work in.
    //
    // An 820px frame sat in the dead zone: too wide to be given the phone
    // layout, too narrow for the desktop one it received. The left rail took
    // ~145px, the remaining column went two-up at ~320px, and every label
    // with an ellipsis truncated — property names collapsed to "S B", every
    // document read "Tenancy contr…". 1024px clears the 900px breakpoint with
    // room the desktop layout was actually designed for.
    //
    // If you change this width, do not drop it below ~1000px.
    //
    //   ?device=landscape  the same tablet on its side, 1366 x 1024
    //   ?device=phone      430 x 932 handset, phone layout forced, for a film
    //                      that wants a hand-held shot
    //
    // NOTHING HERE REACHES THE LIVE APP. All of it hangs off `filming`, which
    // is true only on a `--video-demo*.web.app` preview channel (isFilmMode in
    // demoMode.ts). On portal.investmenter.com and in the store builds there
    // is no frame and no forced layout at all — the app sizes itself to
    // whatever device the owner is actually holding.
    //
    // `?phone=` still tunes the on-screen size, for either device:
    //   ?phone=off   no frame at all, the ordinary desktop layout
    //   ?phone=560   rendered 560px wide on screen
    //   ?phone=fit   as tall as the window allows (whole device in one shot)
    const read = (k: string) => {
      try { return (new URLSearchParams(window.location.search).get(k) || '').toLowerCase(); }
      catch { return ''; }
    };
    const param = read('phone');
    const device = read('device');
    const phone = device === 'phone';
    const landscape = device === 'landscape';

    // The screen the layout inside the frame is written for.
    const W = phone ? 430 : landscape ? 1366 : 1024;
    const H = phone ? 932 : landscape ? 1024 : 1366;

    const apply = () => {
      // 820 is a constant, not the frame width: below it the window is
      // already tablet-shaped or smaller, so the app's own breakpoints are
      // already right and a frame would constrain the layout twice.
      // The frame is for filming ON A COMPUTER. On a real tablet or phone
      // (touch is the primary pointer) the app fills the device and uses its
      // own breakpoints — an iPad is wider than 820, so width alone framed it.
      // ?phone=on forces the frame anyway, e.g. on a touch-screen laptop.
      let touchDevice = false;
      try { touchDevice = window.matchMedia('(pointer: coarse)').matches; } catch { /* old browser: assume computer */ }
      const wide = window.innerWidth > 820 && param !== 'off' && (!touchDevice || param === 'on');
      const root = document.documentElement;
      root.classList.toggle('film-frame', wide);
      // The PHONE layout is forced only for the phone. A tablet frame that
      // carried this would render a stretched handset instead of an iPad.
      root.classList.toggle('film-phone', wide && phone);
      if (!wide) {
        root.style.removeProperty('--film-zoom');
        root.style.removeProperty('--film-w');
        root.style.removeProperty('--film-h');
        return;
      }
      root.style.setProperty('--film-w', `${W}px`);
      root.style.setProperty('--film-h', `${H}px`);
      // The device has to fit the window WHOLE — a frame with its edge cut
      // off is worse than no frame — so the fit takes the smaller of the two
      // axes. Height alone was enough while every frame was portrait; a
      // landscape tablet is wider than it is tall, and on most screens it is
      // the WIDTH that runs out first. ?phone=<n> overrides with an explicit
      // on-screen width.
      const frameW = W + 26;   // screen + the 13px bezel either side
      const frameH = H + 26;
      const fitZoom = Math.min(
        (window.innerWidth - 40) / frameW,
        (window.innerHeight - 40) / frameH,
      );
      const zoom = /^\d+$/.test(param)
        ? Math.min(2.6, Math.max(0.4, Number(param) / W))
        : Math.min(2.2, Math.max(0.4, fitZoom));
      root.style.setProperty('--film-zoom', String(Math.round(zoom * 100) / 100));
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      const root = document.documentElement;
      root.classList.remove('film-frame', 'film-phone');
      root.style.removeProperty('--film-zoom');
      root.style.removeProperty('--film-w');
      root.style.removeProperty('--film-h');
    };
  }, [filming]);

  const selectRole = useCallback((r: AppRole) => {
    // Kept for this session only — no persistence, so relaunching the
    // app always shows the buttons first.
    try {
      sessionStorage.setItem(ROLE_STORAGE_KEY, r);
    } catch {
      /* ignore */
    }
    setRole(r);
  }, []);

  const changeRole = useCallback(() => {
    try {
      sessionStorage.removeItem(ROLE_STORAGE_KEY);
      // Clear any legacy persisted role from earlier builds.
      localStorage.removeItem(ROLE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setRole(null);
    // Back to wherever this visit started: a staff member who came in on
    // ?roles=all returns to the list, everyone else to the welcome screen.
    setShowRoles(entryAsksForRoleList());
  }, []);

  if (!role) {
    if (showRoles) return <RoleSelectScreen onSelect={selectRole} />;
    return (
      <WelcomeScreen
        // "Get Started" always continues as an owner. A tenant corrects that
        // on the sign-in itself, with RoleSwitch — which is the whole point of
        // moving the choice onto the login screen.
        onStart={() => selectRole('owner')}
      />
    );
  }

  switch (role) {
    case 'tenant':
      return (
        <TenantPortal
          demoMode={filming || undefined}
          onChangeRole={changeRole}
          onSwitchRole={selectRole}
        />
      );
    case 'guest':
      return <GuestPortal onChangeRole={changeRole} />;
    case 'ceo':
      return <CEOPortal onChangeRole={changeRole} />;
    case 'partner':
      return <PartnerPortal onChangeRole={changeRole} />;
    case 'demo':
      return <DemoShell onChangeRole={changeRole} />;
    case 'owner':
    default:
      // Standalone PM preview (from the owner login "Preview" button) —
      // reads the emulator, never the live backend.
      if (ownerPreview) {
        return <OwnerPMPreview onBack={() => setOwnerPreview(false)} onChangeRole={changeRole} />;
      }
      // Existing app, unchanged flow, plus the new Property Management tab.
      return (
        <InvestorPortal
          demoMode={filming || undefined}
          onChangeRole={changeRole}
          onSwitchRole={selectRole}
          onPreviewOwnerPM={() => setOwnerPreview(true)}
        />
      );
  }
};

export default RootRouter;
